import React from "react";
import { CloseCircle, Danger, InfoCircle, TickCircle } from "iconsax-react";
import styled from "styled-components";

import { Md3Button } from "../Md3Button";
import { ModalAlertPanel, ModalShell } from "../ModalShell";

export type CustomModalVariant = "default" | "success" | "warning" | "error";

export type CustomModalOption = {
  text: string;
  onPress: () => void;
  style?: "default" | "cancel" | "destructive";
};

export type CustomModalProps = {
  open: boolean;
  title: string;
  message: string;
  variant?: CustomModalVariant;
  options: CustomModalOption[];
  onClose?: () => void;
};

const Body = styled(ModalAlertPanel)`
  display: flex;
  flex-direction: column;
  align-items: stretch;
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
`;

const VariantIcon = styled.div<{ $color: string }>`
  width: 44px;
  height: 44px;
  border-radius: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: color-mix(in srgb, ${({ $color }) => $color} 12%, transparent);
  color: ${({ $color }) => $color};
`;

const Title = styled.h2<{ $tone?: string }>`
  margin: 0;
  font-family: ${({ theme }) => theme.font.familyHeading};
  font-size: 24px;
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ $tone, theme }) => $tone ?? theme.colors.textTitle};
  text-align: center;
  line-height: 1.3;
`;

const Message = styled.p`
  margin: 0 0 16px;
  font-size: 19px;
  line-height: 32px;
  color: var(--color-text-secondary);
  text-align: center;
  overflow-wrap: anywhere;
  word-break: break-word;
`;

const Options = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`;

function variantIcon(variant: CustomModalVariant) {
  switch (variant) {
    case "success":
      return TickCircle;
    case "warning":
      return Danger;
    case "error":
      return CloseCircle;
    default:
      return InfoCircle;
  }
}

function variantColor(
  variant: CustomModalVariant,
  colors: {
    success: string;
    warning: string;
    danger: string;
    info: string;
  }
) {
  switch (variant) {
    case "success":
      return colors.success;
    case "warning":
      return colors.warning;
    case "error":
      return colors.danger;
    default:
      return colors.info;
  }
}

function titleTone(variant: CustomModalVariant): string | undefined {
  if (variant === "error") return "var(--color-danger)";
  if (variant === "warning") return "var(--color-warning)";
  if (variant === "success") return "var(--color-success)";
  return undefined;
}

function mapOption(option: CustomModalOption): {
  variant: "filled" | "outlined" | "text";
  color: "primary" | "secondary" | "danger";
} {
  if (option.style === "destructive") {
    return { variant: "filled", color: "danger" };
  }
  if (option.style === "cancel") {
    return { variant: "text", color: "secondary" };
  }
  return { variant: "filled", color: "primary" };
}

export function CustomModal({
  open,
  title,
  message,
  variant = "default",
  options,
  onClose,
}: CustomModalProps) {
  return (
    <ModalShell open={open} onClose={onClose} maxWidth={520} aria-label={title}>
      <Body>
        <Header>
          {variant !== "default" ? (
            <VariantIcon
              $color={variantColor(variant, {
                success: "var(--color-success)",
                warning: "var(--color-warning)",
                danger: "var(--color-danger)",
                info: "var(--color-info)",
              })}
            >
              {React.createElement(variantIcon(variant), {
                size: 28,
                variant: "Bold",
              })}
            </VariantIcon>
          ) : null}
          <Title $tone={titleTone(variant)}>{title}</Title>
        </Header>

        <Message>{message}</Message>

        <Options>
          {options.map((option) => {
            const mapped = mapOption(option);
            return (
              <Md3Button
                key={option.text}
                title={option.text}
                variant={mapped.variant}
                color={mapped.color}
                size="large"
                fullWidth
                onClick={option.onPress}
              />
            );
          })}
        </Options>
      </Body>
    </ModalShell>
  );
}
